<template>
  <v-layout
    v-if="isSeries || (purchase.eventDate && purchase.eventDate.isEventActive)"
    class="purchase-wrapper"
    role="main"
    aria-label="Event Ticket Purchase"
  >
    <v-snackbar color="error" v-model="snackbar" :timeout="5000" location="top">
      {{ snackbarMsg }}
    </v-snackbar>
    <div
      class="purchase-scroll-area"
      :class="{ 'purchase-scroll-area--cart-open': isCartOpen }"
    >
      <div class="sticky-event-title">
        <event-title
          :title="purchase.eventDate?.eventName!"
          :wide="true"
          titleSize="small"
          :topRightText="
            isSeries
              ? seriesDateRange
              : formatEventTime(
                  purchase.eventDate?.eventStartDateTimeLocal?.toString(),
                )
          "
          :bottomRightText="purchase.eventDate?.eventLocation!"
          icon="fal fa-calendar-day"
          class="purchase-create"
          background-color="black"
          icon-color="white"
          :purchase="purchase"
          :showCartIcon="true"
          :cartOpen="isCartOpen"
          @update:cartOpen="isCartOpen = $event"
        >
          <template #default> </template>
        </event-title>
      </div>
      <div class="purchase-content">
        <div class="purchase-content-header px-md-3">
          <div class="purchase-content-header-images">
            <v-carousel
              hide-delimiters
              v-if="allEventImages.length > 0"
              height="300"
              :show-arrows="allEventImages.length > 1"
            >
              <v-carousel-item
                v-for="(image, index) in allEventImages"
                :key="`event-image-${index}`"
                :src="image.src"
                :alt="image.alt"
                max-height="300"
              />
            </v-carousel>
          </div>
          <div class="purchase-content-header-info">
            <labeled-section title="Event Details" icon="fal fa-calendar-day">
              <show-more
                :expanded="true"
                v-if="
                  purchase.eventDate.eventShortDescription &&
                  purchase.eventDate.eventLongDescription &&
                  purchase.eventDate.eventShortDescription !=
                    purchase.eventDate.eventLongDescription
                "
              >
                <template #less>
                  <span
                    v-html="
                      sanitizeHtml(
                        purchase.eventDate.eventShortDescription.trim(),
                      )
                    "
                  ></span>
                </template>
                <template #more>
                  <span
                    v-html="
                      sanitizeHtml(
                        purchase.eventDate.eventLongDescription.trim(),
                      )
                    "
                  ></span>
                </template>
              </show-more>
              <span
                v-else
                v-html="
                  sanitizeHtml(
                    (
                      purchase.eventDate.eventShortDescription ||
                      purchase.eventDate.eventLongDescription ||
                      ''
                    ).trim(),
                  )
                "
                style="white-space: pre-wrap"
              ></span>
            </labeled-section>
          </div>
        </div>
        <v-card-text class="px-0 pt-0 px-md-3">
          <v-stepper
            v-model="step"
            role="navigation"
            aria-label="Checkout Steps"
          >
            <v-stepper-window
              v-if="step === 1"
              aria-label="Step 1: Select Tickets"
            >
              <v-form ref="form1" v-model="isValidStep1">
                <v-text-field
                  name="fooName"
                  style="display: none"
                  v-model="doNotFillSpot"
                  aria-hidden="true"
                  tabindex="-1"
                  autocomplete="off"
                />

                <labeled-section
                  title="Tickets"
                  icon="fal fa-tickets"
                  icon-class="mr-3"
                >
                  <purchase-pass-quantity-selector
                    v-if="isSeries"
                    :purchase="purchase"
                    :setQuantityCaller="service.setSeriesPassTypeQuantity"
                  />
                  <purchase-quantity-selector
                    v-else
                    :purchase="purchase"
                    :isPublic="true"
                    :isSjSeatMap="
                      seatMapContext?.seatMap?.method ===
                      SeatMapMethodEnum.SoftJourn
                    "
                    @price-selected="handlePriceSelected"
                    @price-cleared="handlePriceCleared"
                  >
                    <template #purchase-seat-map>
                      <purchase-seat-map
                        :purchase="purchase"
                        :context="
                          seatMapContext as PurchaseSeatMapContext | null
                        "
                        :service="service"
                        @context-updated="handleContextUpdate"
                      />
                    </template>
                  </purchase-quantity-selector>
                  <purchase-expiration-notice
                    :purchase="purchase"
                    class="mt-3"
                  />
                  <v-input
                    class="ticket-selection-error pt-2"
                    :rules="
                      !purchase.tickets?.length
                        ? [
                            () =>
                              isSeries
                                ? 'Please select at least one pass.'
                                : 'Please select at least one ticket.',
                          ]
                        : []
                    "
                    role="alert"
                    aria-live="polite"
                    aria-label="Ticket selection validation"
                  ></v-input>
                </labeled-section>

                <v-checkbox
                  name="term"
                  style="display: none"
                  v-model="fakeCheckbox"
                  aria-hidden="true"
                  tabindex="-1"
                />
              </v-form>
              <div
                :class="{ hidden: isValidStep1 != false }"
                class="text-error font-weight-bold text-right"
              >
                Please resolve the errors above before continuing.
              </div>
              <v-row no-gutters class="flex-nowrap">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  :disabled="goingToCheckout"
                  size="large"
                  @click="goToCheckout()"
                >
                  Checkout
                  <v-icon end aria-hidden="true">fal fa-arrow-right</v-icon>
                </v-btn>
              </v-row>
            </v-stepper-window>

            <v-stepper-window
              v-if="step === 2"
              aria-label="Step 2: Contact Information and Payment"
            >
              <div
                ref="contact-information-anchor"
                class="contact-info-anchor"
              ></div>
              <v-form ref="form2" v-model="isValidStep2" validate-on="lazy">
                <purchase-expiration-notice :purchase="purchase" class="mb-8" />
                <labeled-section
                  title="Contact Information"
                  icon="fal fa-id-card"
                >
                  <c-loader-status
                    :loaders="{ 'no-progress': [purchase.$save] }"
                  >
                    <purchase-contact-input
                      ref="contactInfo"
                      :purchase="purchase"
                      v-model="validContactInfo"
                    >
                      <!-- Story #1974 / #3111: Ticket Personalization between contact info and sign-up for marketing -->
                      <template #between-contact-and-marketing>
                        <ticket-personalization
                          v-if="purchase.tickets && purchase.tickets.length > 0"
                          :purchase="purchase"
                          :service="service"
                        />
                      </template>
                    </purchase-contact-input>
                  </c-loader-status>
                </labeled-section>

                <labeled-section
                  v-if="purchase.dietaryRestrictions?.length"
                  title="Dietary Restrictions"
                  icon="fal fa-utensils"
                >
                  <purchase-dietary-input
                    :purchase="purchase"
                  ></purchase-dietary-input>
                </labeled-section>
                <purchase-protection-selector :purchase="purchase" />
                <purchase-addon-selector :purchase="purchase" />
                <labeled-section title="Promo Code" icon="fal fa-badge-dollar">
                  <purchase-discount-code-input
                    :purchase="purchase"
                    class="mt-n3 mb-0"
                  />

                  <purchase-discount-summary :purchase="purchase" />
                </labeled-section>
                <labeled-section title="Summary" icon="fal fa-receipt">
                  <div
                    class="text-h6 font-weight-light mb-3"
                    style="line-height: 1.1"
                  >
                    {{ purchase.eventDate?.eventName }}
                    <div class="d-sm-none"></div>
                    <span class="d-none d-sm-inline">&mdash;</span>

                    <template v-if="isSeries">{{ seriesDateRange }}</template>
                    <template v-else>
                      <!-- • -->
                      <c-display
                        :model="purchase.eventDate"
                        for="eventStartDateTimeLocal"
                        format="MMMM do, yyyy @ h:mm a"
                      />
                      (<c-display
                        :model="purchase.eventDate"
                        for="venueTimezone"
                      />).
                    </template>
                  </div>
                  <purchase-price-summary
                    :purchase="purchase"
                    hideEmptyPaymentsSection
                  />
                </labeled-section>
                <labeled-section
                  v-if="
                    !purchase.eventDate.isFreeEvent &&
                    Array.isArray(purchase.tickets) &&
                    purchase.tickets.length > 0 &&
                    purchase.grandTotal != 0
                  "
                  title="Payment"
                  icon="fal fa-credit-card"
                >
                  <raw-credit-card-input
                    @setCCDetails="setCCDetails"
                    :purchase="purchase"
                    :paymentId="-1"
                    require-full-details
                  >
                  </raw-credit-card-input>
                  <p class="text-center mt-2 mb-0">
                    Canadian cardholders: If your postal code is not accepted,
                    please contact the venue directly for assistance.
                  </p>
                </labeled-section>
                <labeled-section
                  v-if="
                    Array.isArray(purchase.tickets) &&
                    purchase.tickets.length > 0 &&
                    purchase.grandTotal != 0
                  "
                >
                  <p>
                    Please note: Charges will display on your bank statement as
                    Ticket Goat.
                  </p>
                  <p>
                    By completing this purchase, you are transacting with Ticket
                    Goat (Hagadone Technologies), the merchant of record. Ticket
                    Goat processes payments and manages ticket fulfillment and
                    pre-event inquiries on behalf of the event organizer
                  </p>
                </labeled-section>
                <labeled-section
                  v-if="
                    !Array.isArray(purchase.tickets) ||
                    purchase.tickets.length === 0
                  "
                >
                  <strong>
                    Your shopping cart has no tickets. Please go back and add
                    tickets.
                  </strong>
                </labeled-section>
                <hr />
                <br />
                <v-dialog
                  v-model="showTGTermDialog"
                  max-width="640"
                  v-if="staticData.BANK_STATEMENT_NOTIFICATION_ENABLED"
                >
                  <template v-slot:activator="{}">
                    <div class="d-flex flex-column">
                      <ul style="padding-left: 20px; list-style-type: disc">
                        <li>
                          Ticket Goat's
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue"
                            >terms and conditions</a
                          >.
                        </li>
                        <li v-if="purchase.venueDisclaimer">
                          Venue's
                          <a
                            :href="`/terms/venue/${purchase.venueId}`"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-blue"
                            >terms and conditions</a
                          >.
                        </li>
                      </ul>

                      <v-checkbox
                        id="terms-agreement-checkbox"
                        :rules="[
                          acceptedAgreementPurchase ||
                            'Please read and check the agreement box before completing purchase.',
                        ]"
                        v-model="acceptedAgreementPurchase"
                        class="purchase-agreement"
                        aria-describedby="terms-agreement-description"
                        v-if="
                          staticData.BANK_STATEMENT_NOTIFICATION_ENABLED ||
                          purchase.venueDisclaimer
                        "
                      >
                        <template v-slot:label>
                          <p
                            id="terms-agreement-description"
                            style="padding: 0 !important"
                          >
                            By selecting the checkbox, you have read and
                            accepted the above terms and conditions.
                          </p>
                        </template>
                      </v-checkbox>
                    </div>
                  </template>
                  <v-card>
                    <v-card-title>
                      Ticket Goat's Terms and Conditions
                    </v-card-title>
                    <v-card-text>
                      Ticket sales for this event are processed by Ticket Goat,
                      a Hagadone Technologies platform. Ticket Goat serves as
                      the merchant of record for all transactions and is
                      responsible for payment processing, ticket delivery, and
                      pre-event customer support on behalf of the Event
                      Organizer. All refund requests and ticket-related
                      inquiries prior to the event are managed by Ticket Goat in
                      accordance with policies established in coordination with
                      the Event Organizer. The Event Organizer remains
                      responsible for the event itself, including its execution,
                      changes, cancellations, and any post-event disputes. By
                      purchasing, you acknowledge that Ticket Goat acts solely
                      as the ticketing and payment facilitator and is not
                      responsible for the underlying event.
                    </v-card-text>
                    <v-card-actions>
                      <v-btn
                        color="primary"
                        variant="text"
                        @click="
                          () => {
                            showTGTermDialog = false;
                          }
                        "
                      >
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-dialog
                  v-model="showVenueTermDialog"
                  v-if="purchase.venueDisclaimer"
                  max-width="640"
                >
                  <v-card>
                    <v-card-title> Venue's Terms and Conditions </v-card-title>
                    <v-card-text>
                      <span
                        v-html="sanitizeHtml(purchase.venueDisclaimer)"
                      ></span>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn
                        color="primary"
                        variant="text"
                        @click="
                          () => {
                            showVenueTermDialog = false;
                          }
                        "
                      >
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-form>

              <c-loader-status
                :loaders="{ '': [completePayment] }"
                class="mb-10"
              >
                <div class="text-right">
                  <div
                    :class="{ hidden: isValidStep2 != false }"
                    class="text-error font-weight-bold"
                  >
                    Please resolve the errors above before continuing.
                  </div>
                  <v-alert v-if="captchaError" type="error" variant="outlined">
                    {{ captchaError }}
                  </v-alert>

                  <v-row no-gutters>
                    <v-btn
                      class="my-2"
                      color="primary"
                      variant="outlined"
                      size="large"
                      @click="step = 1"
                    >
                      <v-icon start aria-hidden="true"
                        >fal fa-arrow-left</v-icon
                      >
                      Back
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      class="my-2"
                      color="primary"
                      size="large"
                      @click="continueToPayment()"
                      :loading="completePayment.isLoading"
                      v-if="
                        Array.isArray(purchase.tickets) &&
                        purchase.tickets.length > 0
                      "
                    >
                      <v-icon start aria-hidden="true">fal fa-check</v-icon>
                      Complete Purchase
                    </v-btn>
                  </v-row>
                </div>
              </c-loader-status>
            </v-stepper-window>
          </v-stepper>
        </v-card-text>
      </div>
    </div>
    <shopping-cart
      v-model="isCartOpen"
      :purchase="purchase"
      :context="seatMapContext as PurchaseSeatMapContext | null"
      :service="service"
      :hide-proceed-to-checkout="step === 2"
      @proceed-to-checkout="handleCartProceedToCheckout"
    />
  </v-layout>
  <div v-else>
    <v-alert type="error" variant="outlined">
      An error has occurred, please contact the Venue directly.
    </v-alert>
  </div>
</template>

<style lang="scss">
.purchase-create {
  .v-stepper {
    box-shadow: none !important;
  }

  .v-stepper__content {
    padding: 0 !important;
  }

  .ticket-selection-error {
    font-weight: bold;

    .v-messages {
      font-size: 14px;
      margin: auto;
    }
  }

  .purchase-agreement {
    .v-input__slot {
      align-items: flex-start;
    }
  }
}

.v-checkbox.purchase-agreement {
  margin-top: 10px;

  .v-label {
    white-space: wrap !important;
  }
}

.sticky-event-title {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.purchase-wrapper {
  // v-layout = scoped Vuetify layout. v-main inside auto-applies padding-right
  // for permanent drawer via --v-layout-right, so content stays clear of cart.

  // Desktop: scroll area scrolls; reserve cart width via padding when open
  @media (min-width: 960px) {
    height: calc(100vh - var(--v-layout-top, 0px));
    overflow: hidden;

    .purchase-scroll-area {
      flex: 1 1 auto;
      min-width: 0;
      height: 100%;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      &--cart-open {
        padding-right: 380px;

        @media (min-width: 1920px) {
          padding-right: 500px;
        }
      }
    }
  }

  // Mobile: natural flow, page scrolls
  @media (max-width: 959px) {
    display: block;
    overflow-y: visible;
    height: auto;

    .purchase-scroll-area {
      overflow: visible;
      height: auto;
    }
  }
}
.purchase-content {
  position: relative;
  z-index: 1; // Ensures content is below the sticky header

  .purchase-content-header {
    display: flex;
    flex-direction: row;
    gap: 1.3rem;

    .purchase-content-header-images {
      width: 50%;
      img {
        width: 100%;
        height: 100%;
      }
    }
    .purchase-content-header-info {
      width: 50%;
    }
  }

  @media (width < 960px) {
    .purchase-content-header {
      flex-direction: column;

      .purchase-content-header-images {
        width: 100%;
      }
      .purchase-content-header-info {
        width: 100%;
        padding: 1.5rem;
      }
    }
  }
}
.contact-info-anchor {
  position: relative;
  height: 1px; /* Give it a small height so it has a position */
  pointer-events: none;
}
</style>

<script setup lang="ts">
import { ref, computed, useTemplateRef } from "vue";
import { useDisplay } from "vuetify";
import EventTitle from "@common/purchase/event-title.vue";
import PurchaseQuantitySelector from "@common/purchase/purchase-quantity-selector.vue";
import PurchasePassQuantitySelector from "@common/purchase/purchase-pass-quantity-selector.vue";
import PurchaseAddonSelector from "@common/purchase/purchase-addon-selector.vue";
import PurchaseProtectionSelector from "@common/purchase/purchase-protection-selector.vue";
import PurchasePriceSummary from "@common/purchase/purchase-price-summary.vue";
import { formatSeriesStartEndRange } from "@common/purchase/series-cart";
import PurchaseContactInput from "@common/purchase/purchase-contact-input.vue";
import PurchaseDiscountSummary from "@common/purchase/purchase-discount-summary.vue";
import PurchaseExpirationNotice from "@common/purchase/purchase-expiration-notice.vue";
import PurchaseDiscountCodeInput from "@common/purchase/purchase-discount-code-input.vue";
import RawCreditCardInput from "@common/purchase/raw-credit-card-input.vue";
import PurchaseDietaryInput from "@common/purchase/purchase-dietary-input.vue";
import TicketPersonalization from "@common/purchase/ticket-personalization.vue";
import PurchaseSeatMap from "@public/components/purchase-seat-map.vue";
import ShoppingCart from "@common/purchase/shopping-cart.vue";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
  EventDateDtoViewModel,
} from "@public/viewmodels.g";
import { executeRecaptcha } from "@public/captcha-utils";
import { VForm } from "vuetify/components";
import {
  reloadIfNoRequestsPending,
  sanitizeHtml,
  scrollToTop,
  formatEventTime,
} from "@common/utils";
import { beginCheckout, purchaseComplete } from "@common/analytics";
import router from "@public/router";
import {
  CreditCardDetails,
  EventPriceDto,
  SeatMapMethodEnum,
} from "@public/models.g";
import useStaticData from "@common/composables/useStaticData";
import { PurchaseSeatMapContext } from "@common/seat-map-context";
import { eventBus, EventTypes } from "../../eventBus";
import { ProvideInjectConsts } from "@common/provideInjectConsts";

const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  service: PublicPurchaseServiceViewModel;
}>();

const anchor = useTemplateRef("contact-information-anchor");
const form1 = ref<VForm>();
const form2 = ref<VForm>();
const step = ref(1);
const hasVisitedStep2 = ref(false);
const acceptedAgreementPurchase = ref(false);
const doNotFillSpot = ref("");
const fakeCheckbox = ref(false);
const display = useDisplay();

const isCartOpen = ref(
  // Series (eventSeriesId set) have no single eventDate, so key off series-ness directly here.
  // `isSeries` computed is declared below, so inline the check to avoid a temporal-dead-zone ref.
  ((props.purchase.eventSeriesId != null ||
    props.purchase.eventDate?.isSeated) &&
    display.mdAndUp.value)!,
);

// Series reuse this whole flow; branch only where they differ from single-date.
const isSeries = computed(() => props.purchase.eventSeriesId != null);

// A purchase is only ever one type, so resolve its completion endpoint once.
const completePayment = computed(() =>
  isSeries.value
    ? props.service.completeSeriesPayment
    : props.service.completePublicCardPayment,
);

const seriesDateRange = computed(() =>
  formatSeriesStartEndRange(
    props.purchase.eventSeries?.startDate,
    props.purchase.eventSeries?.endDate,
  ),
);

const snackbarMsg = ref("");
const snackbar = ref(false);
const showTGTermDialog = ref(false);
const showVenueTermDialog = ref(false);

const isValidStep1 = ref<boolean | null>(null);
const validContactInfo = ref<boolean | null>(null);
const isValidStep2 = ref<boolean | null>(null);
const contactInfo = ref<typeof PurchaseContactInput>();

const captchaError = ref("");

//for new purchases, this should default to true
props.purchase.isAllowTextMessaging = true;

// used to prevent multiple clicks on checkout button
const goingToCheckout = ref(false);

// each child component can register a callback that must return true
// before proceeding to next step; this allows each child to notify it's finished
// so parent can proceed
const checkoutCallbacks = ref(new Map());

// allow children to register/unregister their checkout callbacks
provide(
  ProvideInjectConsts.RegisterCheckoutCallback,
  (id: string, fn: () => Promise<boolean>) => {
    checkoutCallbacks.value.set(id, fn);
  },
);
provide(ProvideInjectConsts.UnRegisterCheckoutCallback, (id: string) => {
  checkoutCallbacks.value.delete(id);
});

async function validateForm1() {
  isValidStep1.value = (await form1.value?.validate())?.valid ?? false;
  return isValidStep1.value;
}

async function validateForm2() {
  validContactInfo.value =
    (await contactInfo.value?.validate())?.valid ?? false;

  isValidStep2.value =
    (validContactInfo.value && (await form2.value?.validate())?.valid) ?? false;
  return isValidStep2.value;
}

const staticData = useStaticData();

// This is not ideal, but already spent too much time to get the backend grab the right data for this
// and still cannot figure out how to get the EventImage via the ticket purchase instance
// that has EventDate included as a query param. So this is just another API call...
// TODO: will need to implement the way to get nested relational data without making this explicit call
const currentEventDateDto = new EventDateDtoViewModel();

//persist checkout step in the event of page refresh
watch(step, (newStep) => {
  localStorage.setItem(
    props.purchase.publicId! + "_purchaseCheckoutStep",
    String(newStep),
  );

  // When step changes to 2 on mobile, scroll to contact information
  if (newStep === 2 && display.mdAndUp.value === false) {
    nextTick(() => {
      scrollToContactInfoAnchor();
    });
  }
});

onMounted(() => {
  const savedStep = localStorage.getItem(
    props.purchase.publicId! + "_purchaseCheckoutStep",
  );
  if (savedStep && !isNaN(Number(savedStep))) {
    step.value = Number(savedStep);
  }

  // If we're already on step 2 on mobile, scroll to contact information
  if (step.value === 2 && display.mdAndUp.value === false) {
    nextTick(() => {
      scrollToContactInfoAnchor();
    });
  }

  // Subscribe showMessageEvent from eventBus
  eventBus.on(
    EventTypes.UnselectedSeatMapSectionClicked,
    showNeedsSelectTicketTypeMessage,
  );
});

onBeforeUnmount(() => {
  // Unsubscribe showMessageEvent from eventBus
  eventBus.off(
    EventTypes.UnselectedSeatMapSectionClicked,
    showNeedsSelectTicketTypeMessage,
  );
});

currentEventDateDto.$load(props.purchase.eventDate?.id || undefined);

props.purchase.$saveMode = "whole";
props.purchase.$save.setConcurrency("debounce");
props.purchase.$useAutoSave({ wait: 500 });

props.purchase.$save.onRejected(() => {
  reloadIfNoRequestsPending(props.purchase, props.purchase.$save);
});

// Manually reload if continue to payment fails.
props.service.completePublicCardPayment.onRejected(() => {
  props.purchase.$load();
});
props.service.completeSeriesPayment.onRejected(() => {
  props.purchase.$load();
});

let ccDetails: CreditCardDetails[] = [];

function setCCDetails(details: CreditCardDetails[]) {
  ccDetails = details;
}

async function goToCheckout() {
  try {
    goingToCheckout.value = true;
    await nextStep();
  } finally {
    goingToCheckout.value = false;
  }
}

/**
 * Ensure each registered checkout callback return true
 */
async function childComponentsClearForCheckout() {
  if (checkoutCallbacks.value.size > 0) {
    const results = await Promise.all(
      [...checkoutCallbacks.value.values()].map((fn) => {
        return fn();
      }),
    );

    return results.every((r) => r);
  }
  return true;
}

async function nextStep() {
  const ready = await childComponentsClearForCheckout();
  if (!ready) {
    return;
  }

  // passEventPriceMinimum is EventPrice-specific; series enforce per-pass max in the row.
  if (!isSeries.value && !(await passEventPriceMinimum())) {
    return;
  }

  // reloading purchase to ensure we have the latest data
  await props.purchase.$load();
  if (!(await validateForm1())) {
    return;
  }

  if (!hasVisitedStep2.value) {
    hasVisitedStep2.value = true;
    beginCheckout(props.purchase);
  }

  if (doNotFillSpot.value.length > 0 || fakeCheckbox.value) {
    await props.service.markTicketPurchaseHoneyPotSpam(props.purchase.publicId);
    await props.purchase.$load();
  }

  step.value++;

  // Only scroll to top on desktop, on mobile we'll handle scrolling in the step watcher
  if (display.mdAndUp.value) {
    scrollToTop(0, "auto");
  }
}

async function passEventPriceMinimum(): Promise<boolean> {
  let result = true;
  let eventPrices = props.purchase.eventDate?.eventPrices || [];
  for (let eventPrice of eventPrices) {
    let maxPurchaseQuantity = eventPrice.maximumPurchaseQuantity!;
    // Check if minimum is configured
    // The last addition is needed because right after user increase the quantity the availableSeat get updated immediately
    if (
      eventPrice.minimumQuantity != null &&
      eventPrice.minimumQuantity > 0 &&
      props.purchase.tickets &&
      eventPrice.minimumQuantity! <=
        props.purchase.eventDate?.availableSeats! +
          props.purchase.tickets.length
    ) {
      // Check if the amount of ticket reached the minimum requirement
      let countMin = eventPrice.minimumQuantity;
      props.purchase.tickets?.map((ticket) => {
        if (ticket.eventPriceId == eventPrice.id) {
          countMin--;
        }
      });
      // The second argument will make sure it will skip if buyer not buying any tickets from the particular ticket type
      if (countMin > 0 && countMin < eventPrice.minimumQuantity) {
        showSnackBar(
          `Ticket ${eventPrice.displayName} requires a minimum purchase of ${eventPrice.minimumQuantity}. You currently have ${eventPrice.minimumQuantity - countMin}.`,
        );
        isCartOpen.value = true; // cart will be displayed when this message is shown
        result = false;
      }
      // Check that we're not over the maxPurchaseQuantity if maxPurchaseQuantity is non-zero
    } else if (maxPurchaseQuantity > 0) {
      let tickets = props.purchase.tickets || [];
      let purchaseCount = tickets.filter((ticket) => {
        return ticket.eventPriceId == eventPrice.id;
      }).length;
      if (purchaseCount > maxPurchaseQuantity) {
        showSnackBar(
          `Cannot purchase more than ${maxPurchaseQuantity} for ticket ${eventPrice.displayName}. You have ${purchaseCount}`,
        );
        result = false;
      }
    }
  }
  return result;
}

function showNeedsSelectTicketTypeMessage() {
  showSnackBar(
    "You need to choose a ticket type, then click on 'Select' in order to select seats/purchase tickets",
  );
}

function showSnackBar(message: string) {
  snackbar.value = true;
  snackbarMsg.value = message;
}

function hideSnackBar() {
  snackbar.value = false;
}

async function continueToPayment() {
  if (!(await validateForm2()) || !acceptedAgreementPurchase.value) {
    return;
  }
  const complete = completePayment.value;
  captchaError.value = "";
  let captchaToken = "";

  // reCAPTCHA implementation
  if (staticData.RECAPTCHA_SITE_KEY) {
    try {
      captchaToken = await executeRecaptcha(
        staticData.RECAPTCHA_SITE_KEY,
        "complete_purchase",
      );
    } catch (error) {
      console.error("reCAPTCHA failed", error);

      // Stop the purchase flow here.
      // Show user-safe error.
      captchaError.value =
        "We were unable to verify this purchase request. Please refresh the page and try again.";

      return;
    }
  }

  try {
    await complete(props.purchase.publicId, ccDetails[0], captchaToken);
  } catch {
    return;
  }

  if (!complete.wasSuccessful) return;

  const redirectUrl = complete.result!;

  if (redirectUrl === null) {
    // Server will send back a redirect of null
    // to indicate that the purchase details should be shown.
    // Since we're already at the purchase details,
    // just reload the purchase.
    await props.purchase.$load();
    purchaseComplete(props.purchase);
    nextTick(() => scrollToTop(0, "auto"));
    return;
  }

  router.push({
    name: "ticket-purchase-redirecting",
    params: {
      publicId: props.purchase.publicId!,
    },
    query: {
      redirectUrl: redirectUrl,
    },
  });

  window.location.href = redirectUrl;
}

const allEventImages = computed(() => {
  const images = [];
  const eventName = props.purchase.eventDate?.eventName || "Event";

  // Add banner image first if it exists
  if (currentEventDateDto.eventBannerImage) {
    images.push({
      src: currentEventDateDto.eventBannerImage,
      alt: `${eventName} Banner Image`,
    });
  }

  // Add additional event images if they exist
  if (
    currentEventDateDto.eventImages &&
    currentEventDateDto.eventImages.length > 0
  ) {
    currentEventDateDto.eventImages.forEach((eventImage, idx) => {
      images.push({
        src: eventImage.imageSrc!,
        // Use database alt text if available, otherwise use formatted fallback with index
        // The database has an altText image field but it is populated with filename.jpg
        // I don't think this is good enough so we're using a fallback.
        // TODO: User story to change admin flow so the altText can be added to each image.
        // alt: eventImage.altText || `${eventName} Gallery Image ${idx + 1}`,
        alt: `${eventName} Gallery Image ${idx + 1}`,
      });
    });
  }
  return images;
});

function handleCartProceedToCheckout() {
  if (step.value === 1) {
    goToCheckout();
  }
}

const seatMapContext = ref<PurchaseSeatMapContext | null>(null);

// Helper function to scroll to contact information anchor
function scrollToContactInfoAnchor() {
  if (anchor.value) {
    const rect = anchor.value.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const absoluteTop = rect.top + scrollTop;
    const header = document.querySelector(".sticky-event-title");
    const headerHeight = header?.clientHeight || 0;

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      try {
        window.scrollTo({
          top: absoluteTop - headerHeight,
          behavior: "smooth",
        });
      } catch (error) {
        console.error("Scroll error:", error);
      }
    }, 100);
  }
}

function handleContextUpdate(newContext: PurchaseSeatMapContext | null) {
  seatMapContext.value = newContext;
}

function handlePriceSelected(eventPrice: EventPriceDto, sectionName?: string) {
  if (seatMapContext.value) {
    seatMapContext.value.isInSelectMode = true;
    seatMapContext.value.highlightedPriceId = eventPrice.id;
    seatMapContext.value.highlightedSectionName = sectionName ?? null;
  }
  hideSnackBar();
}

async function handlePriceCleared() {
  if (seatMapContext.value) {
    seatMapContext.value.isInSelectMode = false;
    seatMapContext.value.highlightedPriceId = null;
    seatMapContext.value.highlightedSectionName = null;
  }
  await passEventPriceMinimum();
}
</script>
